import { lazy, startTransition, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import Header from './components/Header';
import ClientReview from './components/ClientReview';
import Hero from './components/Hero';
// Landing sections below the hero.
import PremiumLanding from './components/PremiumLanding';
import DiveInSection from './components/DiveInSection';
import ContactSection from './components/ContactSection';
// ── Kept for non-landing app views ────────────────────────────────────────
import GeneratorForm, { initialForm } from './components/GeneratorForm';
import SavedCampaigns from './components/SavedCampaigns';
import ContactForm from './components/ContactForm';
import ScrollTop from './components/ScrollTop';
import Toaster, { setToastNotificationsEnabled, toast } from './components/Toaster';
import AuthModal from './components/AuthModal';
import AccountPanel from './components/AccountPanel';
import ConsentManager, { openCookiePreferences } from './components/ConsentManager';
import LegalCenter from './components/LegalCenter';
import PasswordResetDialog from './components/PasswordResetDialog';
import AuthInteractionGate from './components/AuthInteractionGate';
import ConfirmDialog from './components/ConfirmDialog';
import AppErrorBoundary from './components/AppErrorBoundary';
import './LandingTheme.css';
import './AccountCompliance.css';
import { DEFAULT_SETTINGS, SettingsContext, isRtlLanguage, loadSettings, saveSettings, translate } from './settings';
import { TutorialProvider } from './onboarding/TutorialProvider';
import { useDomTranslator } from './domTranslator';
// ── Lenis smooth scroll + GSAP ticker bridge ──────────────────────────────
import {
  cancelGenerationJob,
  fetchCampaigns,
  generateCampaign,
  generateCampaignAsync,
  mergeCampaignResults,
  normalizeCampaignResult,
  waitForGenerationJob,
  generateExperiment,
  saveCampaign,
  deleteCampaign,
  fetchAccountPreferences,
  recordPrivacyConsent,
  updateAccountPreferences,
} from './services/api';
import { clearLocalSession, getInitialAuthState, signOut, subscribeToAuthChanges } from './services/supabase';
import { attachGenerationTiming } from './utils/generationTiming';

const ResultDashboard = lazy(() => import('./components/ResultDashboard'));
const ExperimentDashboard = lazy(() => import('./components/ExperimentDashboard'));
const FeedbackPanel = lazy(() => import('./components/FeedbackPanel'));
const ImageEditorPage = lazy(() => import('./components/ImageEditorPage'));
const BrandHub = lazy(() => import('./components/BrandHub'));
const Dashboard = lazy(() => import('./components/Dashboard'));

const pendingCampaignJobKey = (userId) => `adpulse.pending-campaign-job.${userId || 'anonymous'}`;
const pendingCampaignStartedAtKey = (userId) => `adpulse.pending-campaign-started-at.${userId || 'anonymous'}`;

function ProductViewLoader() {
  return (
    <div className="product-view-loader" role="status" aria-live="polite">
      <span />
      <div>
        <strong>Opening your workspace</strong>
        <small>Preparing the creative tools…</small>
      </div>
    </div>
  );
}

function CampaignApp() {
  const [view, setView] = useState('landing');
  const isLanding = view === 'landing';
  const [heroVisible, setHeroVisible] = useState(true);
  const [deferHeroCanvas, setDeferHeroCanvas] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [campaign, setCampaign] = useState(null);
  const campaignRef = useRef(campaign);
  campaignRef.current = campaign;
  const [experiment, setExperiment] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [deletingCampaignId, setDeletingCampaignId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMode, setLoadingMode] = useState('');
  const [generationJob, setGenerationJob] = useState(null);
  const [generationJobId, setGenerationJobId] = useState('');
  const [generationStartedAtMs, setGenerationStartedAtMs] = useState(null);
  const [stoppingGeneration, setStoppingGeneration] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [accountSection, setAccountSection] = useState('');
  const [legalOpen, setLegalOpen] = useState('');
  const [passwordResetRequested] = useState(() => (
    new URLSearchParams(window.location.search).get('reset-password') === '1'
    || new URLSearchParams(window.location.hash.replace(/^#/, '')).get('type') === 'recovery'
  ));
  const [passwordResetStatus, setPasswordResetStatus] = useState(() => (
    passwordResetRequested ? 'checking' : 'closed'
  ));
  const [settings, setSettings] = useState(() => loadSettings('visitor'));
  const [settingsOwnerId, setSettingsOwnerId] = useState('');
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const [reviewCampaignId] = useState(() => new URLSearchParams(window.location.search).get('review') || '');
  const recoveringJobRef = useRef('');
  const partialResultJobRef = useRef('');
  const generationStartedAtRef = useRef(null);
  const stopRequestedJobRef = useRef('');

  useEffect(() => {
    document.documentElement.lang = settings.language;
    document.documentElement.dir = isRtlLanguage(settings.language) ? 'rtl' : 'ltr';
    document.documentElement.dataset.theme = settings.theme;
    setToastNotificationsEnabled(settings.notifications);
  }, [settings]);

  useEffect(() => {
    if (!authReady) return undefined;
    let active = true;
    if (!user) {
      setSettingsOwnerId('');
      setSettingsHydrated(false);
      setSettings(loadSettings('visitor'));
      return undefined;
    }

    setSettingsOwnerId(user.id);
    setSettingsHydrated(false);
    fetchAccountPreferences()
      .then((data) => {
        if (!active) return;
        const preferences = data.preferences || {};
        setSettings({
          language: preferences.language || DEFAULT_SETTINGS.language,
          theme: preferences.theme || DEFAULT_SETTINGS.theme,
          notifications: preferences.notifications !== false,
        });
        setSettingsHydrated(true);
      })
      .catch(() => {
        if (!active) return;
        setSettings(DEFAULT_SETTINGS);
        setSettingsHydrated(true);
      });
    return () => { active = false; };
  }, [authReady, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) {
      saveSettings(settings, 'visitor');
      return undefined;
    }
    if (!settingsHydrated || settingsOwnerId !== user.id) return undefined;
    saveSettings(settings, user.id);
    const timer = window.setTimeout(() => {
      updateAccountPreferences(settings).catch((err) => {
        toast(err?.message || 'Could not sync preferences.', 'error');
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [settings, settingsHydrated, settingsOwnerId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLanding || typeof IntersectionObserver === 'undefined') return undefined;

    const hero = document.getElementById('home');
    if (!hero) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { rootMargin: '120px 0px' },
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, [isLanding]);

  const settingsValue = useMemo(() => ({
    settings,
    updateSetting: (key, value) => setSettings((current) => ({ ...current, [key]: value })),
    resetSettings: () => setSettings(DEFAULT_SETTINGS),
    t: (key) => translate(settings.language, key),
  }), [settings]);

  const t = settingsValue.t;
  useDomTranslator(settings.language);

  useEffect(() => {
    let active = true;
    let invalidResetTimer = null;
    getInitialAuthState()
      .then(({ user: initialUser, passwordRecovery }) => {
        if (!active) return;
        setUser(initialUser);
        if (passwordResetRequested) {
          if (passwordRecovery) {
            setPasswordResetStatus('ready');
          } else {
            invalidResetTimer = window.setTimeout(() => {
              if (active) setPasswordResetStatus((current) => (current === 'checking' ? 'invalid' : current));
            }, 1200);
          }
        }
      })
      .catch(() => { if (active) setUser(null); })
      .finally(() => { if (active) setAuthReady(true); });
    const unsubscribe = subscribeToAuthChanges(({ event, user: nextUser }) => {
      if (active) {
        setUser(nextUser);
        setAuthReady(true);
        if (event === 'PASSWORD_RECOVERY') {
          if (invalidResetTimer) window.clearTimeout(invalidResetTimer);
          setPasswordResetStatus('ready');
        }
      }
    });
    return () => {
      active = false;
      if (invalidResetTimer) window.clearTimeout(invalidResetTimer);
      unsubscribe();
    };
  }, [passwordResetRequested]);

  const closePasswordReset = () => {
    setPasswordResetStatus('closed');
    const url = new URL(window.location.href);
    ['reset-password', 'code', 'type', 'error', 'error_code', 'error_description'].forEach((key) => url.searchParams.delete(key));
    if (new URLSearchParams(url.hash.replace(/^#/, '')).get('type') === 'recovery') url.hash = '';
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  useEffect(() => {
    if (authReady && user) loadSavedCampaigns();
    if (authReady && !user) setCampaigns([]);
  }, [authReady, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authReady || !user?.id) return undefined;
    const storageKey = pendingCampaignJobKey(user.id);
    const startedAtStorageKey = pendingCampaignStartedAtKey(user.id);
    const pendingJobId = window.localStorage.getItem(storageKey) || '';
    if (!pendingJobId || recoveringJobRef.current === pendingJobId) return undefined;

    let active = true;
    recoveringJobRef.current = pendingJobId;
    partialResultJobRef.current = '';
    const storedStartedAt = Number(window.localStorage.getItem(startedAtStorageKey));
    if (Number.isFinite(storedStartedAt) && storedStartedAt > 0) {
      beginGenerationTiming(storedStartedAt);
    }
    setLoading(true);
    setLoadingMode('single');
    setGenerationJobId(pendingJobId);
    setError('');
    waitForGenerationJob(pendingJobId, {
      maxWaitMs: 2 * 60 * 60 * 1000,
      onUpdate: (job, payload) => {
        if (active) applyGenerationJobUpdate(job, payload);
      },
      isStopRequested: () => Boolean(stopRequestedJobRef.current),
    })
      .then((recoveredCampaign) => {
        if (!active || !recoveredCampaign) return;
        window.localStorage.removeItem(storageKey);
        window.localStorage.removeItem(startedAtStorageKey);
        setCampaign(completeGenerationTiming(normalizeCampaignResult(recoveredCampaign)));
        setExperiment(null);
        setSaved(false);
        setView('workspace');
        toast('Completed campaign restored', 'success');
        scrollToResults();
      })
      .catch((err) => {
        if (!active) return;
        const failed = String(err?.job?.status || '').toLowerCase() === 'failed';
        const stopped = Boolean(err?.cancelled) || Boolean(stopRequestedJobRef.current);
        if (failed || stopped || Number(err?.status) === 404) {
          window.localStorage.removeItem(storageKey);
          window.localStorage.removeItem(startedAtStorageKey);
        }
        resetGenerationTiming();
        if (stopped) return;
        setError(err?.message || 'Could not restore the pending campaign generation.');
      })
      .finally(() => {
        if (!active) return;
        recoveringJobRef.current = '';
        setLoading(false);
        setLoadingMode('');
        setStoppingGeneration(false);
        stopRequestedJobRef.current = '';
      });

    return () => { active = false; };
  }, [authReady, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadSavedCampaigns() {
    try {
      const data = await fetchCampaigns();
      const list = data.campaigns || [];
      setCampaigns(list);
      if (reviewCampaignId) {
        const sharedCampaign = list.find((item) => item.id === reviewCampaignId);
        if (sharedCampaign) {
          resetGenerationTiming();
          setCampaign(sharedCampaign);
          setExperiment(null);
          setSaved(true);
          setView('workspace');
        }
      }
    } catch (err) {
      setCampaigns([]);
    }
  }

  function handleAuth(loggedInUser) {
    setUser(loggedInUser);
  }

  async function handleSignOut() {
    try {
      await signOut();
      setUser(null);
      resetGenerationTiming();
      setCampaign(null);
      setExperiment(null);
      setCampaigns([]);
      toast('Signed out', 'info');
    } catch (err) {
      toast(err?.message || 'Could not sign out', 'error');
    }
  }

  const handleViewChange = (newView) => {
    if (newView === view) return;
    if (newView === 'landing') {
      setDeferHeroCanvas(true);
      setView(newView);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return;
    }
    setDeferHeroCanvas(false);
    startTransition(() => setView(newView));
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  };

  function scrollToResults() {
    window.setTimeout(() => {
      document.querySelector('.cs-output, .experiment-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }

  function beginGenerationTiming(startedAt = Date.now()) {
    const value = Number(startedAt);
    const normalized = Number.isFinite(value) && value > 0 ? value : Date.now();
    generationStartedAtRef.current = normalized;
    setGenerationStartedAtMs(normalized);
  }

  function resetGenerationTiming() {
    generationStartedAtRef.current = null;
    setGenerationStartedAtMs(null);
  }

  function completeGenerationTiming(generatedCampaign) {
    const timedCampaign = attachGenerationTiming(
      generatedCampaign,
      generationStartedAtRef.current,
      Date.now(),
    );
    resetGenerationTiming();
    return timedCampaign;
  }

  function applyGenerationJobUpdate(job, payload) {
    setGenerationJob(job);
    if (!generationStartedAtRef.current) {
      const jobStartedAt = Date.parse(job?.created_at || job?.started_at || '');
      if (Number.isFinite(jobStartedAt)) beginGenerationTiming(jobStartedAt);
    }
    const partialCampaign = normalizeCampaignResult(payload);
    const partialAssets = partialCampaign?.creative_assets || {};
    const hasImages = Boolean(
      partialAssets.portrait_post_url
      || partialAssets.full_screen_ad_url
      || partialAssets.image_variants?.some?.((item) => item?.image_url)
    );
    const hasReviewDraft = Boolean(partialCampaign?.claim_verification?.semantic_status === 'unavailable'
      && partialCampaign?.ad_headline && partialCampaign?.input);
    if (!partialCampaign || (!hasImages && !hasReviewDraft)) return;

    setCampaign((current) => mergeCampaignResults(current, partialCampaign));
    setExperiment(null);
    setSaved(false);
    setView('workspace');
    const status = String(job?.status || '').toLowerCase();
    const active = !['completed', 'failed'].includes(status);
    if (active && hasImages && partialResultJobRef.current !== job?.id) {
      partialResultJobRef.current = job?.id || 'partial';
      toast(
        partialCampaign.input?.generate_video
          ? 'Images ready — video generation continues in the background'
          : 'Images ready',
        'success',
      );
      scrollToResults();
    }
  }

  async function handleGenerate(event) {
    event.preventDefault();
    if (!user) {
      setAuthOpen(true);
      return;
    }
    beginGenerationTiming();
    setLoading(true);
    setLoadingMode('single');
    setGenerationJob(null);
    setGenerationJobId('');
    setStoppingGeneration(false);
    stopRequestedJobRef.current = '';
    partialResultJobRef.current = '';
    setCampaign(null);
    setError('');
    setSaved(false);
    setExperiment(null);
    const generationPayload = {
      ...form,
    };
    delete generationPayload.gemini_model;

    try {
      let generated;
      let asyncStarted = false;
      try {
        generated = await generateCampaignAsync(generationPayload, {
          onJobStart: (job) => {
            asyncStarted = true;
            setGenerationJob(job);
            setGenerationJobId(job?.id || '');
            if (job?.id && user?.id) {
              window.localStorage.setItem(pendingCampaignJobKey(user.id), job.id);
              window.localStorage.setItem(
                pendingCampaignStartedAtKey(user.id),
                String(generationStartedAtRef.current || Date.now()),
              );
            }
            toast('Campaign job queued', 'info');
          },
          onUpdate: (job, payload) => applyGenerationJobUpdate(job, payload),
          isStopRequested: () => Boolean(stopRequestedJobRef.current),
        });
      } catch (asyncErr) {
        // Keep local development forgiving for older backends that do not have
        // the async queue endpoint, but never start a second generation after a
        // queued job has already begun.
        const asyncEndpointMissing = !asyncStarted && !asyncErr?.asyncJobStarted && [404, 405].includes(Number(asyncErr?.status));
        if (!asyncEndpointMissing) {
          throw asyncErr;
        }
        console.warn('Async generation endpoint unavailable, falling back to sync generation.', asyncErr);
        setGenerationJob(null);
        setGenerationJobId('');
        generated = await generateCampaign(generationPayload);
      }

      if (!generated || typeof generated !== 'object') {
        throw new Error('Generation finished without a displayable campaign result.');
      }
      if (user?.id) {
        window.localStorage.removeItem(pendingCampaignJobKey(user.id));
        window.localStorage.removeItem(pendingCampaignStartedAtKey(user.id));
      }
      setCampaign(completeGenerationTiming(generated));
      toast('Campaign generated!', 'success');
      scrollToResults();
    } catch (err) {
      const stopped = Boolean(err?.cancelled) || Boolean(stopRequestedJobRef.current);
      resetGenerationTiming();
      if (!stopped) {
        const message = err?.message || 'Could not generate campaign. Please check if the backend is running on port 8000.';
        setError(message);
        toast(message, 'error');
      }
    } finally {
      setLoading(false);
      setLoadingMode('');
      setStoppingGeneration(false);
      stopRequestedJobRef.current = '';
    }
  }

  async function handleStopGeneration() {
    if (!loading || stoppingGeneration || !generationJobId) return;
    const jobId = generationJobId;
    stopRequestedJobRef.current = jobId;
    setStoppingGeneration(true);
    setError('');
    // The job must not be resumed on the next sign-in once it has been stopped.
    if (user?.id) {
      window.localStorage.removeItem(pendingCampaignJobKey(user.id));
      window.localStorage.removeItem(pendingCampaignStartedAtKey(user.id));
    }
    try {
      const stopped = await cancelGenerationJob(jobId);
      setGenerationJob(stopped?.job || null);
      // The backend releases the GPU as it unwinds the render it had already
      // started, so the studio is handed back without waiting for that.
      resetGenerationTiming();
      setLoading(false);
      setLoadingMode('');
      toast('Generation stopped and the GPU was released', 'info');
    } catch (err) {
      stopRequestedJobRef.current = '';
      const message = err?.message || 'Could not stop this generation.';
      setError(message);
      toast(message, 'error');
    } finally {
      setStoppingGeneration(false);
    }
  }

  async function handleExperiment() {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    beginGenerationTiming();
    setLoading(true);
    setLoadingMode('experiment');
    setError('');
    setSaved(false);
    const generationPayload = {
      ...form,
    };
    delete generationPayload.gemini_model;

    try {
      const generatedExperiment = await generateExperiment(generationPayload);
      const completedAt = Date.now();
      const timedVariants = (generatedExperiment.variants || []).map((item) => (
        attachGenerationTiming(item, generationStartedAtRef.current, completedAt)
      ));
      const timedWinner = timedVariants.find((item) => item.id === generatedExperiment.winner_id) || timedVariants[0] || null;
      resetGenerationTiming();
      setExperiment(timedWinner ? {
        ...generatedExperiment,
        variants: timedVariants,
      } : generatedExperiment);
      setCampaign(timedWinner);
      scrollToResults();
    } catch (err) {
      resetGenerationTiming();
      setError(err?.message || 'Could not run prompt experiment. Please check if the backend is running on port 8000.');
    } finally {
      setLoading(false);
      setLoadingMode('');
    }
  }

  function handleSelectVariant(variant) {
    setCampaign(variant);
    setSaved(false);
    document.querySelector('.cs-output')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleLoadSaved(savedCampaign) {
    if (!loading) resetGenerationTiming();
    setCampaign(savedCampaign);
    setExperiment(null);
    setSaved(true);
    setError('');
    scrollToResults();
  }

  async function handleDeleteSaved(savedCampaign) {
    if (!user || !savedCampaign?.id) {
      setAuthOpen(true);
      return;
    }
    setDeletingCampaignId(savedCampaign.id);
    setError('');
    try {
      await deleteCampaign(savedCampaign.id);
      setCampaigns((current) => current.filter((item) => item.id !== savedCampaign.id));
      if (campaign?.id === savedCampaign.id) {
        resetGenerationTiming();
        setCampaign(null);
        setExperiment(null);
        setSaved(false);
      }
      toast('Campaign deleted', 'success');
    } catch (err) {
      const message = err?.message || 'Could not delete this campaign.';
      setError(message);
      toast(message, 'error');
    } finally {
      setDeletingCampaignId('');
    }
  }

  function handleUseTemplate(template) {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setForm({ ...initialForm, ...template });
    resetGenerationTiming();
    setCampaign(null);
    setExperiment(null);
    setSaved(false);
    setError('');
    window.requestAnimationFrame(() => {
      document.querySelector('.brief-studio')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    toast('Campaign starter loaded', 'success');
  }

  function handleCampaignUpdate(updated) {
    setCampaign(updated);
    setSaved(false);
    setExperiment(null);
  }

  async function handleSave() {
    if (!campaign) return;
    if (!user) {
      setAuthOpen(true);
      return;
    }
    try {
      const snapshot = campaign;
      await saveCampaign(snapshot);
      if (campaignRef.current === snapshot) setSaved(true);
      toast('Campaign saved', 'success');
      await loadSavedCampaigns();
    } catch (err) {
      setError('Could not save campaign locally. Please check the backend terminal.');
      toast('Could not save campaign', 'error');
    }
  }

  return (
    <SettingsContext.Provider value={settingsValue}>
    <TutorialProvider user={user} currentView={view} onNavigate={handleViewChange}>
    <main className={`app-shell ${isLanding ? 'is-landing' : 'is-product'} view-${view} theme-${settings.theme}`}>
      {/* Premium Cyber Grid & Light Beams animated background */}
      <div className="animated-bg">
        <div className="animated-bg-beams" />
      </div>

      <Header
        currentView={view}
        onChangeView={handleViewChange}
        user={user}
        onSignIn={() => setAuthOpen(true)}
        onSignOut={handleSignOut}
        onOpenAccount={(section) => setAccountSection(section || 'profile')}
      />
      
      <div className="app-view-stack">
        {isLanding && (
          <div className="app-view-pane lp-page">
            {/* ── Hero: UNTOUCHED — all animations start after this ─── */}
            <Hero
              active={isLanding && heroVisible}
              deferCanvas={deferHeroCanvas}
              onStartProject={() => handleViewChange('workspace')}
            />

            <PremiumLanding onStartProject={() => handleViewChange('workspace')} />

            <DiveInSection onStartProject={() => handleViewChange('workspace')} />

            <ContactSection>
              <ContactForm onOpenLegal={setLegalOpen} />
            </ContactSection>
          </div>
        )}
        {!isLanding && <AppErrorBoundary
          key={view}
          sectionName={view === 'editor' ? 'Graphics Suite' : view === 'brandhub' ? 'Brand Hub' : view === 'dashboard' ? 'Analytics' : 'Campaign Studio'}
          resetLabel="Return to Campaign Studio"
          onReset={() => handleViewChange('workspace')}
        ><Suspense fallback={<ProductViewLoader />}>{view === 'editor' ? (
          <section id="editor-view" className="sandbox-section" style={{ padding: '20px 0' }}>
            <AuthInteractionGate authenticated={Boolean(user)} onSignIn={() => setAuthOpen(true)} label="Sign in to edit campaign assets">
              <ImageEditorPage
                campaigns={campaigns}
                currentCampaign={campaign}
                onBack={() => handleViewChange('workspace')}
              />
            </AuthInteractionGate>
          </section>
        ) : view === 'brandhub' ? (
          <section id="brandhub-view" className="sandbox-section" style={{ padding: '20px 0' }}>
            <AuthInteractionGate authenticated={Boolean(user)} onSignIn={() => setAuthOpen(true)} label="Sign in to add brand data and files">
              <BrandHub />
            </AuthInteractionGate>
          </section>
        ) : view === 'dashboard' ? (
          <section id="dashboard-view" className="campaign-page">
            <Dashboard onStart={() => handleViewChange('workspace')} />
          </section>
        ) : (
          <section id="sandbox" className="campaign-page">
            <div className="campaign-page-head">
              <p className="eyebrow">{t('app.workspaceEyebrow')}</p>
              <h2>{t('app.workspaceTitle')}</h2>
              <p>{t('app.workspaceDescription')}</p>
            </div>

            <AuthInteractionGate authenticated={Boolean(user)} onSignIn={() => setAuthOpen(true)} label="Sign in before entering campaign data or generating content">
              <div className="campaign-top">
                <GeneratorForm
                  form={form}
                  setForm={setForm}
                  loading={loading}
                  loadingMode={loadingMode}
                generationJob={generationJob}
                  generationJobId={generationJobId}
                  campaign={campaign}
                  generationStartedAtMs={generationStartedAtMs}
                  onGenerate={handleGenerate}
                  onExperiment={handleExperiment}
                  onStopGeneration={handleStopGeneration}
                  stoppingGeneration={stoppingGeneration}
                />
                <SavedCampaigns
                  campaigns={campaigns}
                  onSelect={handleLoadSaved}
                  onUseTemplate={handleUseTemplate}
                  onDelete={handleDeleteSaved}
                  deletingCampaignId={deletingCampaignId}
                />
              </div>
            </AuthInteractionGate>

            {error && <div className="campaign-error">{error}</div>}

            <ExperimentDashboard
              experiment={experiment}
              selectedCampaignId={campaign?.id}
              onSelectVariant={handleSelectVariant}
            />

            <ResultDashboard
              campaign={campaign}
              generationInProgress={loading}
              generationJob={generationJob}
              onStopGeneration={handleStopGeneration}
              stoppingGeneration={stoppingGeneration}
              canStopGeneration={loading && Boolean(generationJobId)}
              onSave={handleSave}
              saved={saved}
              onUpdateCampaign={handleCampaignUpdate}
              reviewMode={Boolean(reviewCampaignId && campaign?.id === reviewCampaignId)}
            />

          {!(reviewCampaignId && campaign?.id === reviewCampaignId) && <FeedbackPanel campaign={campaign} />}
        </section>
        )}</Suspense></AppErrorBoundary>}
      </div>

      <footer className="footer-note">
        <p style={{ margin: '0 0 10px 0' }}>© 2026 AWN GmbH. {t('app.footerRights')}</p>
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted-soft)' }}>
          AWN AdPulse - {t('app.footerTagline')}
        </p>
        <nav className="footer-legal-links" aria-label={t('app.legalLinksLabel')}>
          <button type="button" onClick={() => setLegalOpen('legal')}>{t('app.legalNotice')}</button>
          <button type="button" onClick={() => setLegalOpen('privacy')}>{t('app.privacyPolicy')}</button>
          <button type="button" onClick={() => setLegalOpen('cookies')}>{t('app.cookiePolicy')}</button>
          <button type="button" onClick={() => setLegalOpen('terms')}>{t('app.terms')}</button>
          <button type="button" onClick={openCookiePreferences}>{t('app.cookiePreferences')}</button>
        </nav>
      </footer>

      <ScrollTop />
      <Toaster />
      <ConfirmDialog />
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onAuth={handleAuth}
        onOpenLegal={setLegalOpen}
      />
      <AccountPanel
        open={Boolean(accountSection)}
        initialSection={accountSection || 'profile'}
        user={user}
        onClose={() => setAccountSection('')}
        onOpenSettings={() => {
          setAccountSection('');
          window.requestAnimationFrame(() => window.dispatchEvent(new Event('adpulse:open-settings')));
        }}
        onOpenLegal={setLegalOpen}
        onProfileUpdated={(profile) => setUser((current) => current ? ({
          ...current,
          name: profile.full_name || current.name,
          company: profile.company_name || '',
        }) : current)}
        onAccountDeleted={async () => {
          try { await clearLocalSession(); } catch (deleteSessionError) { /* The server-side user is already gone. */ }
          setAccountSection('');
          setUser(null);
          resetGenerationTiming();
          setCampaign(null);
          setExperiment(null);
          setCampaigns([]);
          toast('Your account and stored data were deleted.', 'success');
        }}
      />
      <PasswordResetDialog
        open={passwordResetStatus !== 'closed'}
        status={passwordResetStatus}
        onClose={closePasswordReset}
      />
      <ConsentManager
        onOpenLegal={setLegalOpen}
        onConsentSaved={(consent) => {
          if (user) {
            recordPrivacyConsent({
              consent_version: consent.version,
              functional: consent.functional,
              analytics: consent.analytics,
              marketing: consent.marketing,
              action: consent.action,
            }).catch(() => {});
          } else if (consent.functional) {
            saveSettings(settings, 'visitor');
          }
        }}
      />
      <LegalCenter open={legalOpen} onClose={() => setLegalOpen('')} onSelect={setLegalOpen} />
    </main>
    </TutorialProvider>
    </SettingsContext.Provider>
  );
}

function App() {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('review');
  return token ? <ClientReview token={token} /> : <CampaignApp />;
}

export default App;
